<template>
  <div class="admission-record">
    <!-- 患者信息 -->
    <PatientInfo :patientData="patientData" />
    
    <!-- 动态字段展示（支持引用推荐） -->
    <div v-if="activeFields && activeFields.length > 0" class="fields-container">
      <div
        v-for="(field, index) in activeFields"
        :key="index"
        class="field-container"
        :data-index="index"
      >
        <div class="flex items-start gap-2">
          <!-- 字段标题 -->
          <div class="field-title flex-shrink-0">
            <div class="field-title-row">
              <strong>{{ field.label || field.title }}：</strong>
              <button class="copy-row-btn" title="复制本行" @click.stop="copyField(field)">
                <i class="el-icon-document-copy"></i>
              </button>
            </div>
          </div>
          <!-- 字段内容区域 -->
          <div class="flex-1 min-w-0">
            <!-- 编辑模式 -->
            <div v-if="field.isEdit" class="edit-mode">
              <el-input
                v-model="field.value"
                type="textarea"
                :rows="3"
                placeholder="请输入内容..."
                @blur="handleInputBlur(field, index)"
                @click.stop
                :class="['edit-input', { 'locked-height': !!field.editHeight }]"
                :style="field.editHeight ? { '--locked-edit-height': field.editHeight + 'px' } : null"
              ></el-input>
              <div class="edit-buttons">
                <button
                  @mousedown="cancelEdit(field, index)"
                  class="cancel-edit-btn"
                >取消编辑</button>
              </div>
            </div>

            <!-- 显示模式 -->
            <div v-else>
              <!-- 检查是否有修改建议 -->
              <div v-if="field.isEdit_newValue && field.isEdit_newValue != '未提供'" class="suggestion-mode">
                <!-- 原始内容 - 红框 -->
                <div class="original-content">
                  <div class="original-text-field" @click="editBtn(field, index, $event)" @click.stop>
                    <div class="content-wrapper">
                      <div class="whitespace-pre-wrap content-text" v-if="field.value">{{ field.value }}</div>
                      <div class="placeholder-text" v-else>点击输入内容...</div>
                    </div>
                  </div>
                </div>
                <!-- 建议内容 - 绿色，点击可编辑 -->
                <div class="suggested-content">
                  <!-- 建议编辑模式 -->
                  <div v-if="field.isEditingSuggestion" class="suggestion-edit-mode">
                    <el-input
                      v-model="field.isEdit_newValue"
                      type="textarea"
                      :rows="3"
                      placeholder="请编辑建议内容..."
                      @blur="finishEditSuggestion(field, index)"
                      @click.stop
                      :class="['edit-suggestion-input', { 'locked-height': !!field.suggestionEditHeight }]"
                      :style="field.suggestionEditHeight ? { '--locked-edit-height': field.suggestionEditHeight + 'px' } : null"
                    ></el-input>
                  </div>
                  <!-- 建议内容显示模式 -->
                  <span
                    v-else
                    class="suggested-text clickable-suggestion"
                    @click="editSuggestion(field, index, $event)"
                    @click.stop
                    title="点击编辑建议内容"
                  >
                    {{ field.isEdit_newValue }}
                  </span>
                  <!-- 操作按钮 -->
                  <div class="suggestion-buttons">
                    <button class="accept-btn" @click="handleAccept(field, index)">
                      <i class="el-icon-check"></i>接受
                    </button>
                    <button class="reject-btn" @click="handleRefuse(field, index)">
                      <i class="el-icon-close"></i>拒绝
                    </button>
                  </div>
                </div>
              </div>

              <!-- 正常显示模式 -->
              <div v-else class="editable-field-content" @click="editBtn(field, index, $event)" @click.stop>
                <div class="content-wrapper">
                  <div class="whitespace-pre-wrap content-text" v-if="field.value">{{ field.value }}</div>
                  <div class="placeholder-text" v-else>点击输入内容...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 固定字段（兼容旧数据，仅在确实有旧数据时显示，避免字段加载时的闪现） -->
    <template v-else-if="hasLegacyData">
      <div class="field-item">
        <div class="field-label-with-copy">
          <div class="field-label">主诉：</div>
          <button class="copy-row-btn" title="复制本行" @click.stop="copyLegacyField('主诉', formData.chiefComplaint)">
            <i class="el-icon-document-copy"></i>
          </button>
        </div>
        <div class="field-value">
          <el-input
            type="textarea"
            :rows="2"
            v-model="formData.chiefComplaint"
            placeholder="请输入主诉"
            @blur="handleBlur"
          />
        </div>
      </div>
      
      <div class="field-item">
        <div class="field-label-with-copy">
          <div class="field-label">现病史：</div>
          <button class="copy-row-btn" title="复制本行" @click.stop="copyLegacyField('现病史', formData.presentIllness)">
            <i class="el-icon-document-copy"></i>
          </button>
        </div>
        <div class="field-value">
          <el-input
            type="textarea"
            :rows="3"
            v-model="formData.presentIllness"
            placeholder="请输入现病史"
            @blur="handleBlur"
          />
        </div>
      </div>
      
      <div class="field-item">
        <div class="field-label-with-copy">
          <div class="field-label">既往史：</div>
          <button class="copy-row-btn" title="复制本行" @click.stop="copyLegacyField('既往史', formData.pastHistory)">
            <i class="el-icon-document-copy"></i>
          </button>
        </div>
        <div class="field-value">
          <el-input
            type="textarea"
            :rows="3"
            v-model="formData.pastHistory"
            placeholder="请输入既往史"
            @blur="handleBlur"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PatientInfo from './PatientInfo.vue';

export default {
  name: 'AdmissionRecord',
  components: {
    PatientInfo
  },
  props: {
    patientData: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: () => ({
        chiefComplaint: '',
        presentIllness: '',
        pastHistory: '',
        fields: []
      })
    }
  },
  data() {
    return {
      formData: {
        chiefComplaint: '',
        presentIllness: '',
        pastHistory: ''
      },
      activeFields: [],
      isCancelling: false
    };
  },
  computed: {
    // 判断是否有旧数据（用于决定是否显示固定字段，避免字段加载时的闪现）
    hasLegacyData() {
      return !!(this.formData.chiefComplaint || this.formData.presentIllness || this.formData.pastHistory);
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          // 优先使用 fields（动态字段）
          if (Array.isArray(newVal.fields) && newVal.fields.length > 0) {
            this.activeFields = newVal.fields.map(f => ({
              key: f.key,
              label: f.label || f.title,
              title: f.label || f.title,
              value: f.value || '',
              originalValue: f.value || '',
              isEdit: false,
              // 优先使用父组件显式的 isEdit_newValue（含 null），避免 suggestedValue 把已接受的建议又刷回来
              isEdit_newValue: Object.prototype.hasOwnProperty.call(f, 'isEdit_newValue')
                ? f.isEdit_newValue
                : (f.suggestedValue || null),
              isEditingSuggestion: false
            }));
          } else {
            // 使用固定字段（兼容旧数据）
            this.formData = {
              chiefComplaint: newVal.chiefComplaint || '',
              presentIllness: newVal.presentIllness || '',
              pastHistory: newVal.pastHistory || ''
            };
            this.activeFields = [];
          }
        }
      }
    }
  },
  methods: {
    buildCopyLine(label, value) {
      const cleanLabel = label || "未命名字段";
      const cleanValue = value == null ? "" : String(value).trim();
      return `${cleanLabel}：${cleanValue}`;
    },
    hasLineValue(line) {
      const index = line.indexOf("：");
      return index !== -1 && line.slice(index + 1).trim() !== "";
    },
    async copyText(text, successMsg = "已复制") {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success(successMsg);
      } catch (error) {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.$message.success(successMsg);
      }
    },
    copyField(field) {
      const label = field.label || field.title || "未命名字段";
      const value = field.value == null ? "" : String(field.value).trim();
      if (!value) {
        this.$message.warning("当前字段为空，未复制");
        return;
      }
      this.copyText(this.buildCopyLine(label, value), "已复制当前行");
    },
    copyLegacyField(label, value) {
      const text = value == null ? "" : String(value).trim();
      if (!text) {
        this.$message.warning("当前字段为空，未复制");
        return;
      }
      this.copyText(this.buildCopyLine(label, text), "已复制当前行");
    },
    getCopyLines() {
      if (Array.isArray(this.activeFields) && this.activeFields.length > 0) {
        return this.activeFields
          .map(field => this.buildCopyLine(field.label || field.title, field.value))
          .filter(line => this.hasLineValue(line));
      }
      const legacyLines = [
        this.buildCopyLine("主诉", this.formData.chiefComplaint),
        this.buildCopyLine("现病史", this.formData.presentIllness),
        this.buildCopyLine("既往史", this.formData.pastHistory)
      ];
      return legacyLines.filter(line => this.hasLineValue(line));
    },
    handleBlur() {
      this.$emit('input', { ...this.formData });
      this.$emit('change', { ...this.formData });
      // 触发父组件的保存方法
      this.$emit('save');
    },
    
    // 获取当前数据
    getData() {
      if (this.activeFields.length > 0) {
        return {
          fields: this.activeFields.map(f => ({
            key: f.key,
            label: f.label,
            value: f.value,
            suggestedValue: f.isEdit_newValue
          }))
        };
      }
      return { ...this.formData };
    },

    /* 编辑模式 */
    editBtn(field, index, event) {
      // 先保存当前正在编辑的字段
      const currentEditingIndex = this.activeFields.findIndex(item => item.isEdit);
      if (currentEditingIndex !== -1 && currentEditingIndex !== index) {
        const currentEditingField = this.activeFields[currentEditingIndex];
        this.saveField(currentEditingField, currentEditingIndex, true, false);
      }

      // 记录原始值
      if (field.originalValue === undefined) {
        field.originalValue = field.value;
      }
      const clickTarget = event?.currentTarget || event?.target;
      const fieldContainer = clickTarget?.closest?.('.field-container');
      const textContainer = clickTarget?.closest?.('.editable-field-content, .original-text-field')
        || fieldContainer?.querySelector?.('.editable-field-content, .original-text-field');
      const measuredHeight = textContainer ? Math.ceil(textContainer.getBoundingClientRect().height) : 0;
      const lockedHeight = Math.max(measuredHeight, 70);
      this.$set(this.activeFields, index, { ...field, isEdit: true, editHeight: lockedHeight });
    },

    /* 取消编辑 */
    cancelEdit(field, index) {
      this.isCancelling = true;
      if (field.originalValue !== undefined) {
        field.value = field.originalValue;
        delete field.originalValue;
      }
      this.$set(this.activeFields, index, { ...field, isEdit: false });
      this.$nextTick(() => {
        this.isCancelling = false;
      });
    },

    /* 处理输入框失焦事件 */
    handleInputBlur(field, index) {
      if (this.isCancelling) {
        return;
      }
      const hasChanged = field.originalValue !== undefined && field.value !== field.originalValue;
      if (hasChanged) {
        setTimeout(() => {
          this.saveField(field, index, true, false);
        }, 100);
      } else {
        this.saveField(field, index, false, false);
      }
    },

    /* 保存字段 */
    saveField(field, index, shouldSave = true, isSwitch = false) {
      if (field.originalValue !== undefined) {
        delete field.originalValue;
      }
      if (!isSwitch) {
        this.$set(this.activeFields, index, { ...field, isEdit: false });
      }
      if (shouldSave) {
        // 更新数据并通知父组件
        this.$emit('input', { fields: this.activeFields.map(f => ({
          key: f.key,
          label: f.label,
          value: f.value,
          suggestedValue: f.isEdit_newValue
        })) });
        // 触发父组件的保存方法
        this.$emit('save');
      }
    },

    /* 编辑建议内容 */
    editSuggestion(field, index, event) {
      const clickTarget = event?.currentTarget || event?.target;
      const textContainer = clickTarget?.closest?.('.suggested-text, .suggested-content');
      const measuredHeight = textContainer ? Math.ceil(textContainer.getBoundingClientRect().height) : 0;
      const lockedHeight = Math.max(measuredHeight, 70);
      this.$set(this.activeFields, index, { ...field, isEditingSuggestion: true, suggestionEditHeight: lockedHeight });
    },

    /* 完成建议编辑 */
    finishEditSuggestion(field, index) {
      this.$set(this.activeFields, index, { ...field, isEditingSuggestion: false });
    },

    /* 接受 */
    handleAccept(field, index) {
      const newValue = field.isEdit_newValue;
      this.$set(this.activeFields, index, {
        ...field,
        value: newValue,
        isEdit_newValue: null
      });
      this.$emit('input', { fields: this.activeFields.map(f => ({
        key: f.key,
        label: f.label,
        value: f.value,
        suggestedValue: f.isEdit_newValue,
        isEdit_newValue: f.isEdit_newValue
      })) });
      // 触发保存事件，让父组件知道数据已被修改
      this.$emit('save');
    },

    /* 拒绝 */
    handleRefuse(field, index) {
      this.$set(this.activeFields, index, { ...field, isEdit_newValue: null });
      this.$emit('input', { fields: this.activeFields.map(f => ({
        key: f.key,
        label: f.label,
        value: f.value,
        suggestedValue: f.isEdit_newValue,
        isEdit_newValue: f.isEdit_newValue
      })) });
    }
  }
};
</script>

<style lang="less" scoped>
.admission-record {
  padding: 1rem 1.25rem;
  background: transparent; // 使用透明背景，让父容器的浅灰色背景显示出来
  
  .field-item {
    display: flex;
    align-items: flex-start;
    margin: 0;
    padding: 12px 16px;
    background: transparent; // 改为透明背景，与动态字段保持一致
    border: none;
    border-bottom: 1px solid #e5e5ea;
    border-radius: 0;
    transition: all 0.3s ease;
    
    &:last-child {
      border-bottom: none;
    }
    
    .field-label {
      width: 80px;
      color: #333;
      font-weight: 600;
      line-height: 28px;
      padding-top: 4px;
      text-align: left;
      flex-shrink: 0;
      font-size: 14px;
    }
    
    .field-value {
      flex: 1;
      margin-left: 12px;
      
      ::v-deep .el-textarea {
        width: 100%;
        .el-textarea__inner {
          border: 1px solid #d1d1d6;
          border-radius: 8px;
          resize: vertical;
          width: 100%;
          max-width: 100%;
          white-space: pre-wrap;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
      }
    }
  }

  // 动态字段样式（引用门诊病历样式）
  .fields-container {
    .field-container {
      padding: 0.3rem 0; // 与门诊保持一致，只保留上下间距，没有白色背景和边框

      .field-title {
        width: 92px;
        color: #333;
        font-weight: 600;
        line-height: 28px;
        padding-top: 4px;
        font-size: 14px;

        .field-title-row {
          display: grid;
          grid-template-columns: auto 16px;
          align-items: center;
          column-gap: 4px;
          white-space: nowrap;

          strong {
            white-space: nowrap;
          }
        }
      }

      .edit-mode {
        .edit-input {
          margin-bottom: 8px;
          ::v-deep .el-textarea__inner {
            border-radius: 0.5rem !important; // 与门诊保持一致
            border: 4px solid #4169e1 !important; // 与门诊保持一致
            font-size: 0.95rem;
            padding: 0.5em 0.75rem;
            resize: vertical;
            min-height: 70px;
            width: 100%;
            max-width: 100%;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
        }
        .edit-input.locked-height {
          ::v-deep .el-textarea__inner {
            height: var(--locked-edit-height) !important;
            min-height: var(--locked-edit-height) !important;
          }
        }
        .edit-buttons {
          text-align: right;
          .cancel-edit-btn {
            padding: 4px 12px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            &:hover {
              background: #e5e5e5;
            }
          }
        }
      }

      .suggestion-mode {
        .original-content {
          margin-bottom: 0.75rem;

          .original-text-field {
            background-color: #fef2f2;
            border: 2px solid #fca5a5;
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.95rem;
            color: #991b1b;
            display: block;
            line-height: 1.6;
            min-height: 60px; // 设置最小高度
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;

            &:hover {
              background-color: #fef7f7;
              border-color: #f87171;
            }
          }
        }
        .suggested-content {
          position: relative;
          margin-bottom: 2rem; // 为框外按钮预留空间

          .suggested-text {
            background-color: #f0fdf4;
            color: #166534;
            border: 2px solid #86efac;
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.95rem;
            display: block;
            line-height: 1.6;
            min-height: 2.5rem;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
            cursor: pointer;
          }
          .edit-suggestion-input.locked-height {
            ::v-deep .el-textarea__inner {
              height: var(--locked-edit-height) !important;
              min-height: var(--locked-edit-height) !important;
            }
          }
          .edit-suggestion-input {
            ::v-deep .el-textarea__inner {
              width: 100%;
              max-width: 100%;
              white-space: pre-wrap;
              word-break: break-word;
              overflow-wrap: anywhere;
            }
          }

          .suggestion-buttons {
            position: absolute;
            top: 100%; // 从框的底部开始
            margin-top: 0.25rem; // 在边框线下方留出小间距
            right: 0.5rem; // 框外右侧，与框右对齐
            display: flex;
            gap: 0.25rem;
            z-index: 10; // 确保按钮在最上层

            .accept-btn, .reject-btn {
              font-weight: 500;
              font-size: 0.75rem;
              line-height: 1rem;
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
              padding-left: 0.75rem;
              padding-right: 0.75rem;
              line-height: 26px;
              border: none;
              border-radius: 9999px !important;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
              transition: all 0.3s !important;
              cursor: pointer;

              &:hover {
                cursor: pointer;
                transform: translateY(-3px);
              }

              &:active {
                transform: translateY(-1px);
              }

              i {
                margin-right: 4px;
              }
            }

            .accept-btn {
              background-color: #34c759 !important;
              color: white !important;
            }

            .reject-btn {
              background-color: #d32f2f !important;
              color: white !important;
            }
          }
        }
      }

      .editable-field-content {
        cursor: pointer;
        padding: 0.5em 0.75rem; // 与门诊保持一致
        border-radius: 0.5rem; // 与门诊保持一致，使用更大的圆角
        border: 1px solid #d1d5db; // 与门诊保持一致
        background-color: #ffffff; // 白色背景
        transition: all 0.2s ease;
        min-height: 50px; // 与门诊保持一致
        width: 100%;
        display: block;

        .content-wrapper {
          width: 100%;
          min-height: 20px;
          pointer-events: none;
        }

        &:hover {
          border-color: #888d97; // 与门诊保持一致
          box-shadow: 0 12px 12px rgba(81, 85, 97, 0.15); // 与门诊保持一致
        }

        .content-text {
          white-space: pre-wrap;
          font-size: 0.95rem;
          color: #2c2c2c;
          line-height: 1.6;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .placeholder-text {
          color: #9ca3af; // 与门诊保持一致
          font-style: italic;
          font-size: 0.95rem;
        }
      }
    }
  }

  .copy-row-btn {
    border: none;
    background: transparent;
    color: #4169e1;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .field-label-with-copy {
    width: 92px;
    display: grid;
    grid-template-columns: auto 16px;
    align-items: center;
    column-gap: 4px;
    flex-shrink: 0;
    margin-right: 12px;

    .field-label {
      width: auto;
      padding-top: 0;
      white-space: nowrap;
      line-height: 24px;
    }
  }

  .field-item {
    .field-value {
      margin-left: 0;
      padding-left: 8px;
    }
  }
}
</style>
